import { useMemo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import MonacoEditor from "@monaco-editor/react";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { useModalAutoClose, validateConductanceMatrix, showConductanceErrors, useJsonModalState } from "../hooks/ThermalDesignerHooks";
import { showToastMessage } from "../../../../../../../utils/commonFunctions";
import { DividerLine } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { ThermalModalHeader, ThermalModalFooter } from "../components/common";
import ConfirmationDialog from "../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog";
import {
  exportThermalConductivityAsJson,
  exportThermalConductivityAsCsv,
  readFileAsText,
  parseConductanceCsv,
  applyConductanceCsvToNodes,
  applyConductanceJsonToNodes,
} from "../utils/thermalConfigUtils";

const ThermalConductivityJsonModal = () => {
  const { setOpenModal, isSatDesignerSidebarOpen, nodeSpecsOptions, setNodeSpecsOptions } = useThermalDesign();
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  useModalAutoClose("isThermalConductivityJsonModalOpen");

  const configuredNodes = useMemo(() => {
    return nodeSpecsOptions.filter((n) => n.status === "complete");
  }, [nodeSpecsOptions]);

  const thermalConductivityJson = useMemo(() => {
    const conductivityData: any = {};
    configuredNodes.forEach((node) => {
      conductivityData[`node_${node.id}`] = {
        conductance: node.values?.node_conductance || [],
      };
    });
    return JSON.stringify(conductivityData, null, 2);
  }, [configuredNodes]);

  const { json: jsonInput, setJson: setJsonInput, handleJsonChange, handleJsonBlur, validateJsonStructure, pendingJsonRef } = useJsonModalState(thermalConductivityJson);

  const handleExportJson = () => {
    exportThermalConductivityAsJson(nodeSpecsOptions);
  };

  const handleExportCsv = () => {
    exportThermalConductivityAsCsv(nodeSpecsOptions);
  };

  const handleImport = async (file: File) => {
    try {
      const text = await readFileAsText(file);
      if (file.name.endsWith(".csv")) {
        const matrix = parseConductanceCsv(text);
        const updated = applyConductanceCsvToNodes(matrix, nodeSpecsOptions);
        setNodeSpecsOptions(updated);
      } else {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          setNodeSpecsOptions(parsed);
          setJsonInput(JSON.stringify(parsed, null, 2));
        } else {
          const updated = applyConductanceJsonToNodes(parsed, nodeSpecsOptions);
          setNodeSpecsOptions(updated);
          setJsonInput(JSON.stringify(parsed, null, 2));
        }
      }
      showToastMessage("success", "Conductivity data imported successfully");
    } catch {
      showToastMessage("error", "Import failed: could not parse file");
    }
  };

  const header = {
    title: "Thermal Conductivity JSON",
    isModalMinimized,
    setIsModalMinimized,
    setOpenModal,
    modalItem: "isThermalConductivityJsonModalOpen",
    onExportJson: handleExportJson,
    onExportCsv: handleExportCsv,
    onImport: handleImport,
  };

  const isMinimumNodesConfigured = configuredNodes.length >= 4;

  useEffect(() => {
    setJsonInput(thermalConductivityJson);
    pendingJsonRef.current = null;
  }, [thermalConductivityJson, setJsonInput, pendingJsonRef]);

  const handleCancel = () => {
    setOpenModal((prev) => ({
      ...prev,
      isThermalConductivityJsonModalOpen: false,
    }));
  };

  const handleSaveAndExit = () => {
    try {
      const parsedJson = JSON.parse(jsonInput);
      const nodeIds = configuredNodes.map((n) => `node_${n.id}`);
      const matrix = nodeIds.map((nodeId) => {
        const arr = parsedJson[nodeId]?.conductance;
        return Array.isArray(arr) ? arr : [];
      });
      const errors = validateConductanceMatrix(nodeIds, matrix);
      if (errors.length > 0) {
        showConductanceErrors(errors);
        return;
      }
    } catch {
      showToastMessage("error", "Invalid JSON — please fix the format before saving.");
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = () => {
    try {
      const parsedJson = JSON.parse(jsonInput);
      setNodeSpecsOptions((prev) =>
        prev.map((node) => {
          const nodeKey = `node_${node.id}`;
          if (parsedJson[nodeKey] && Array.isArray(parsedJson[nodeKey].conductance)) {
            return {
              ...node,
              values: {
                ...node.values,
                node_conductance: parsedJson[nodeKey].conductance,
              },
            } as any;
          }
          return node;
        })
      );
      setShowConfirmDialog(false);
      showToastMessage("success", "Conductance matrix updated successfully");
      setOpenModal((prev) => ({
        ...prev,
        isThermalConductivityJsonModalOpen: false,
        isNodeSpecsModalOpen: true,
      }));
    } catch (err) {
      showToastMessage("error", "Failed to update conductance matrix");
      setShowConfirmDialog(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmDialog(false);
  };

  return (
    <div className={`nodeSpecsModal ${!isSatDesignerSidebarOpen ? "left" : "right"}`}>
      <ThermalModalHeader header={header} />
      {!isModalMinimized && (
        <>
          <div className={`nodeSpecsModal__inputs${isMinimumNodesConfigured ? " nodeSpecsModal__inputs--monaco" : ""}`}>
            {!isMinimumNodesConfigured && (
              <div className="nodespecsModal_warning_blue_div">
                Configure at least 4 nodes to get accurate thermal conductivity results.
              </div>
            )}
            {isMinimumNodesConfigured && (
              <MonacoEditor
                height="420px"
                language="json"
                theme="vs-dark"
                value={jsonInput}
                onChange={(value) => handleJsonChange(value ?? "")}
                onMount={(editor) => {
                  editor.onDidBlurEditorWidget(() => handleJsonBlur(jsonInput, () => {}));
                }}
                options={{
                  minimap: { enabled: false },
                  fontSize: 12,
                  fontFamily: "'Fira Code', 'Consolas', monospace",
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  wordWrap: "on",
                  lineNumbers: "on",
                  renderLineHighlight: "all",
                  tabSize: 2,
                  formatOnPaste: true,
                  scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
                }}
              />
            )}
          </div>
          <div className="nodeSpecsModal__divider__div">
            <DividerLine />
          </div>
          <ThermalModalFooter onSave={handleSaveAndExit} isLastNode={true} onCancel={handleCancel} />
        </>
      )}

      {typeof document !== "undefined" &&
        createPortal(
          <ConfirmationDialog
            type="update"
            open={showConfirmDialog}
            title="Update Conductance Matrix"
            description="Are you sure you want to update the conductance matrix? The JSON values will be applied to the thermal node specifications."
            onConfirm={handleConfirmSave}
            onCancel={handleCancelConfirmation}
            confirm_button_text="Update"
            cancel_button_text="Cancel"
          />,
          document.body
        )}
    </div>
  );
};

export default ThermalConductivityJsonModal;
